import React, { useState } from 'react';
import { loginWithCredentials } from '../api/login';
import styles from './KioskLogin.module.css';

interface KioskLoginProps {
  /** 로그인 성공 시 storeId 와 함께 호출 */
  onLoggedIn: (storeId: string) => void;
}

/**
 * reason 코드를 한국어 안내 문구로 변환
 */
function messageForReason(reason?: string): string {
  switch (reason) {
    case 'INVALID_CREDENTIALS':
      return '아이디 또는 비밀번호가 올바르지 않습니다.';
    case 'LOCKED':
    case 'LICENSE_LOCKED':
      return '라이선스가 정지되었습니다. 고객센터로 문의해주세요.';
    case 'PENDING_PAYMENT':
    case 'UNPAID':
      return '라이선스 결제가 확인되지 않았습니다. 결제 후 다시 시도해주세요.';
    default:
      return reason || '로그인에 실패했습니다. 잠시 후 다시 시도해주세요.';
  }
}

/**
 * 키오스크/태블릿용 매장 로그인 화면
 * - 큰 터치 친화 입력 + 만석 남색 브랜딩
 * - 매장 관리자 아이디/비밀번호로 로그인
 */
export function KioskLogin({ onLoggedIn }: KioskLoginProps) {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!loginId.trim() || !password) {
      setError('아이디와 비밀번호를 입력해주세요.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const result = await loginWithCredentials(loginId.trim(), password);
      if (!result.valid || !result.storeId) {
        setError(messageForReason(result.reason));
        return;
      }

      localStorage.setItem('kiosk_store_id', result.storeId);
      if (result.licenseKey) {
        localStorage.setItem('kiosk_license_key', result.licenseKey);
      }
      localStorage.setItem('kiosk_login_id', result.loginId ?? loginId.trim());

      onLoggedIn(result.storeId);
    } catch (err) {
      setError(err instanceof Error ? err.message : '로그인에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <form className={styles.card} onSubmit={handleSubmit}>
        <img src="/banner2.png" alt="만석" className={styles.logo} />
        <h1 className={styles.title}>키오스크 로그인</h1>
        <p className={styles.subtitle}>
          매장 관리자 아이디와 비밀번호로 로그인하면
          <br />
          이 태블릿이 해당 매장의 키오스크로 설정됩니다.
        </p>

        <label className={styles.field}>
          <span className={styles.label}>아이디</span>
          <input
            className={styles.input}
            type="text"
            inputMode="text"
            autoComplete="username"
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            placeholder="매장 관리자 아이디"
            disabled={submitting}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>비밀번호</span>
          <input
            className={styles.input}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="비밀번호"
            disabled={submitting}
          />
        </label>

        {error && <div className={styles.error}>{error}</div>}

        <button className={styles.submit} type="submit" disabled={submitting}>
          {submitting ? '로그인 중...' : '로그인'}
        </button>
      </form>
    </div>
  );
}
